import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import {
  useGetWebsiteChatSessionsQuery,
  useLazyGetWebsiteChatSessionsQuery
} from "../../redux/queries"
import { dateRangeToTimestamps } from "../utils/datetime"
import { DateRangePreset } from "../ui/activity/date-filter"
import { WebsiteChatSession } from "../../types"
import { useUserLastActivity } from "./use-user-last-activity"

const LIMIT = 20
const IDLE_THRESHOLD = 180000
const POLL_INTERVAL = 10000

export const useWebsiteChatConversations = () => {
  const [fetchPage, { isFetching }] = useLazyGetWebsiteChatSessionsQuery()
  const lastActivity = useUserLastActivity()

  const [allSessions, setAllSessions] = useState<WebsiteChatSession[]>([])
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  const isMounted = useRef(false)

  const [searchQuery, setSearchQuery] = useState("")
  const isSearchActive = searchQuery.trim().length > 0

  const { data: pollData } = useGetWebsiteChatSessionsQuery(
    { page: 1, limit: LIMIT },
    {
      pollingInterval: POLL_INTERVAL,
      skip: isSearchActive || Date.now() - lastActivity > IDLE_THRESHOLD
    }
  )

  useEffect(() => {
    if (!pollData?.conversations?.length) return

    setAllSessions(prev => {
      if (prev.length === 0) return prev

      const existingIds = new Set(prev.map(s => s._id))
      const updatedMap = new Map(pollData.conversations.map(s => [s._id, s]))

      const merged = prev.map(s => updatedMap.get(s._id) ?? s)

      const newSessions = pollData.conversations.filter(
        s => !existingIds.has(s._id)
      )

      return newSessions.length > 0 ? [...newSessions, ...merged] : merged
    })
  }, [pollData])

  const fetchAndAppend = useCallback(
    async (pageNum: number, reset = false) => {
      try {
        const result = await fetchPage({ page: pageNum, limit: LIMIT }).unwrap()
        const incoming = result.conversations ?? []
        setAllSessions(prev => (reset ? incoming : [...prev, ...incoming]))
        setHasMore(pageNum < result.totalPages)
        setPage(pageNum + 1)
      } catch {
        // handled by RTK Query
      } finally {
        setIsLoading(false)
      }
    },
    [fetchPage]
  )

  useEffect(() => {
    setIsLoading(true)
    fetchAndAppend(1, true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const loadMore = useCallback(() => {
    if (!hasMore || isFetching) return
    fetchAndAppend(page)
  }, [hasMore, isFetching, page, fetchAndAppend])

  const [selectedBrandId, setSelectedBrandId] = useState("")
  const [datePreset, setDatePreset] = useState<DateRangePreset>("all")
  const [customDateStart, setCustomDateStart] = useState<number | null>(null)
  const [customDateEnd, setCustomDateEnd] = useState<number | null>(null)

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true
      return
    }
    setIsLoading(true)
    setAllSessions([])
    setPage(1)
    setHasMore(false)
    fetchAndAppend(1, true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBrandId])

  const handleDateChange = useCallback(
    (range: DateRangePreset, startDate?: Date, endDate?: Date) => {
      if (range === "custom" && startDate && endDate) {
        setDatePreset("custom")
        setCustomDateStart(startDate.getTime())
        setCustomDateEnd(endDate.getTime())
      } else {
        setDatePreset(range)
        setCustomDateStart(null)
        setCustomDateEnd(null)
      }
    },
    []
  )

  const handleClearDate = useCallback(() => {
    setDatePreset("all")
    setCustomDateStart(null)
    setCustomDateEnd(null)
  }, [])

  const sessions = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    const { min, max } = dateRangeToTimestamps(
      datePreset,
      customDateStart ? new Date(customDateStart) : null,
      customDateEnd ? new Date(customDateEnd) : null,
      { unit: "milliseconds" }
    )

    return allSessions.filter(s => {
      if (q) {
        const id = s.subjectId?.toLowerCase() ?? ""
        if (!id.includes(q)) return false
      }

      if (selectedBrandId && s.brandId !== selectedBrandId) return false

      if (min || max) {
        const t = new Date(s.lastActivityAt).getTime()
        if (min && t < min) return false
        if (max && t > max) return false
      }

      return true
    })
  }, [
    allSessions,
    searchQuery,
    selectedBrandId,
    datePreset,
    customDateStart,
    customDateEnd
  ])

  return {
    sessions,
    isLoading,
    hasMore,
    isFetchingMore: isFetching,
    loadMore,
    searchQuery,
    setSearchQuery,
    selectedBrandId,
    setSelectedBrandId,
    datePreset,
    customDateStart,
    customDateEnd,
    handleDateChange,
    handleClearDate
  }
}
